import { createSlice } from '@reduxjs/toolkit';
import {
  closeReceipt,
  countReceipt,
  fetchReceipt,
  fetchReceivingQueue,
  findReceipt,
  scanReceivingCode,
} from './operatorReceivingThunks';
import { receivingExcessFound, receivingLineChosen } from './operatorReceivingActions';
import { RECEIVING_STEP, countIntent, needsSlotChoice, onlyReceivingSlotCode } from './operatorReceivingUtils';
import { describeCandidates } from '../operator/operatorCandidates';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { intentOf } from '../operator/operatorIdempotency';
import { acceptedCode } from '../operator/operatorScanGate';
import { operatorWarehouseSelected } from '../operator/operatorSlice';
import { isBackgroundRequest } from '../feedback/feedbackSlice';

/*
 * Receiving on the floor: the queue of open receipts, and the one being
 * counted — its step, what is being counted, and what the server is waiting
 * for an answer on.
 */
const initialQueue = {
  items: [],
  count: 0,
  statusCounts: null,
  page: 1,
  hasMore: false,
  loading: false,
  error: null,
  requestId: null,
  /** When its first page last came, by the phone's clock. */
  receivedAt: null,
  /** A scanned guide being looked up. */
  finding: false,
  lastCode: null,
};

/** One visit to a receipt: opening another starts it over, and a trip to the queue does not. */
const initialTask = {
  receiptId: null,
  receipt: null,
  loading: false,
  error: null,
  requestId: null,
  /** Where the goods land, when the operator had to say it. */
  slotCode: null,
  step: RECEIVING_STEP.PRODUCT,
  /** What the quantity step is counting; see `draftFromLine`. */
  draft: null,
  scan: { lastCode: null },
  /** `count` | `close` while one is out. */
  pending: null,
  /**
   * A sheet waiting on the operator: `{kind: 'ambiguous', ...}`, `{kind: 'confirm', ...}` to close short,
   * or `{kind: 'excess', excess, arg}` to count over what a line lacks.
   */
  resolution: null,
  /** `{type, arg, key}` of the write this visit sent last: the only one whose answer it takes. */
  lastWrite: null,
  /** The last count that went through, for "Contar otra igual". */
  lastCount: null,
};

const initialState = {
  queue: initialQueue,
  /** The code of each receipt loaded here, by id: how a band names it once it is off screen. */
  names: {},
  ...initialTask,
};

/**
 * Whether an answer is to the write this visit waits for. Answers of another
 * receipt, or of an earlier visit to this one, are not: the operator can
 * leave a receipt with a write out, and its answer comes back late.
 */
const isAwaited = (state, action) => action.meta.requestId === state.lastWrite?.key;

/** A write starts on the receipt open; one of another — sent again from its band — leaves it alone. */
const startWrite = (state, action, pending, type) => {
  if (action.meta.arg?.receiptId !== state.receiptId) return;
  state.pending = pending;
  state.resolution = null;
  state.lastWrite = { type, arg: intentOf(action.meta.arg), key: action.meta.requestId };
};

/**
 * The receipt an awaited write answered with. It is newer than a reload still
 * out — one an earlier visit's answer asked for — which would undo it.
 */
const takeAnsweredReceipt = (state, receipt) => {
  if (!receipt) return;
  state.receipt = receipt;
  if (state.loading) {
    state.loading = false;
    state.requestId = null;
  }
};

const stepAfterLoad = (state) => {
  if (needsSlotChoice(state.receipt, state.slotCode)) return RECEIVING_STEP.SLOT;
  return state.draft ? RECEIVING_STEP.QUANTITY : RECEIVING_STEP.PRODUCT;
};

const operatorReceivingSlice = createSlice({
  name: 'operatorReceiving',
  initialState,
  reducers: {
    /** A receipt opened. Another receipt starts from nothing; the same one keeps its draft. */
    receivingEntered: (state, action) => {
      if (state.receiptId !== action.payload) Object.assign(state, initialTask, { receiptId: action.payload });
      state.resolution = null;
    },
    /** Leaving must not leave a sheet waiting for when the operator comes back. */
    receivingLeft: (state) => {
      state.resolution = null;
    },
    receivingDraftCancelled: (state) => {
      state.draft = null;
      state.step = RECEIVING_STEP.PRODUCT;
    },
    receivingSlotChangeRequested: (state) => {
      if (state.receipt?.slot_locked) return;
      state.slotCode = null;
      state.draft = null;
      state.step = RECEIVING_STEP.SLOT;
    },
    receivingResolutionDismissed: (state) => {
      state.resolution = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(operatorWarehouseSelected, () => initialState)

      .addCase(fetchReceivingQueue.pending, (state, action) => {
        state.queue.requestId = action.meta.requestId;
        if (isBackgroundRequest(action)) return;
        state.queue.loading = true;
        state.queue.error = null;
      })
      .addCase(fetchReceivingQueue.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.queue.requestId) return;
        const page = action.meta.arg?.page ?? 1;
        const results = action.payload.results || [];
        state.queue.loading = false;
        state.queue.error = null;
        state.queue.page = page;
        state.queue.items = page === 1 ? results : [...state.queue.items, ...results];
        state.queue.count = action.payload.count ?? state.queue.items.length;
        state.queue.statusCounts = action.payload.status_counts || null;
        state.queue.hasMore = Boolean(action.payload.next);
        if (page === 1) state.queue.receivedAt = action.meta.receivedAt;
      })
      .addCase(fetchReceivingQueue.rejected, (state, action) => {
        // A refresh in the background fails in silence: the list stays as it was, and the next one tries again.
        if (action.meta.requestId !== state.queue.requestId || isBackgroundRequest(action)) return;
        state.queue.loading = false;
        state.queue.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(findReceipt.pending, (state) => {
        state.queue.finding = true;
      })
      .addCase(findReceipt.fulfilled, (state, action) => {
        state.queue.finding = false;
        state.queue.lastCode = acceptedCode(action);
      })
      .addCase(findReceipt.rejected, (state, action) => {
        if (!action.meta.condition) state.queue.finding = false;
      })

      .addCase(fetchReceipt.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchReceipt.fulfilled, (state, action) => {
        state.names[action.meta.arg] = action.payload?.code || null;
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.receipt = action.payload;
        state.slotCode = state.slotCode || onlyReceivingSlotCode(action.payload);
        state.step = stepAfterLoad(state);
      })
      .addCase(fetchReceipt.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(scanReceivingCode.fulfilled, (state, action) => {
        state.scan.lastCode = acceptedCode(action);
        const result = action.payload;
        if (result.kind === 'slot') {
          state.slotCode = result.slot.code;
          state.step = RECEIVING_STEP.PRODUCT;
        } else if (result.kind === 'line') {
          state.draft = result.draft;
          state.step = RECEIVING_STEP.QUANTITY;
        } else {
          state.resolution = {
            kind: 'ambiguous',
            origin: 'local',
            message: 'Ese código es de más de una línea: elige cuál.',
            code: result.code,
            options: result.options,
          };
        }
      })
      .addCase(receivingLineChosen, (state, action) => {
        state.draft = action.payload;
        state.step = RECEIVING_STEP.QUANTITY;
        state.resolution = null;
      })
      .addCase(receivingExcessFound, (state, action) => {
        state.resolution = { kind: 'excess', excess: action.payload.excess, arg: action.payload.arg };
      })

      .addCase(countReceipt.pending, (state, action) => startWrite(state, action, 'count', countReceipt.typePrefix))
      .addCase(countReceipt.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        state.pending = null;
        state.lastWrite = null;
        if (!state.receipt) return;
        takeAnsweredReceipt(state, action.payload.receipt);
        // «Contar otra igual» asks again: a confirmation is for the count it was given to.
        state.lastCount = countIntent(action.meta.arg);
        state.draft = null;
        state.step = RECEIVING_STEP.PRODUCT;
      })
      .addCase(countReceipt.rejected, (state, action) => {
        if (action.meta.condition || !isAwaited(state, action)) return;
        state.pending = null;
        const error = action.payload;
        if (error?.kind === OPERATOR_ERROR.AMBIGUOUS) {
          state.resolution = {
            kind: 'ambiguous',
            origin: 'server',
            message: error.message,
            ...describeCandidates(error.candidates),
            arg: intentOf(action.meta.arg),
            key: action.meta.requestId,
          };
        }
        // Nothing was registered: confirming sends it again as another action, under a key of its own.
        if (error?.kind === OPERATOR_ERROR.CONFIRM && error.excess) {
          state.resolution = { kind: 'excess', excess: error.excess, arg: countIntent(action.meta.arg) };
        }
        if (!isRetryable(error)) state.lastWrite = null;
      })

      .addCase(closeReceipt.pending, (state, action) => startWrite(state, action, 'close', closeReceipt.typePrefix))
      .addCase(closeReceipt.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        state.pending = null;
        state.lastWrite = null;
        if (!state.receipt) return;
        takeAnsweredReceipt(state, action.payload.receipt);
      })
      .addCase(closeReceipt.rejected, (state, action) => {
        if (action.meta.condition || !isAwaited(state, action)) return;
        state.pending = null;
        const error = action.payload;
        if (error?.kind === OPERATOR_ERROR.CONFIRM) {
          state.resolution = { kind: 'confirm', message: error.message, missing: error.missing };
        }
        if (!isRetryable(error)) state.lastWrite = null;
      });
  },
});

export const {
  receivingEntered,
  receivingLeft,
  receivingDraftCancelled,
  receivingSlotChangeRequested,
  receivingResolutionDismissed,
} = operatorReceivingSlice.actions;

/** Whether a read code is welcome right now. */
export const selectReceivingAccepting = (state) => {
  const receiving = state.operatorReceiving;
  return (
    Boolean(receiving.receipt) &&
    !receiving.pending &&
    !receiving.resolution &&
    receiving.step !== RECEIVING_STEP.QUANTITY
  );
};

export default operatorReceivingSlice.reducer;
